"use client"

import React, { createContext, useContext, useReducer, useCallback, ReactNode, useEffect } from 'react';
import {
  collection, addDoc, updateDoc, deleteDoc, doc, getDocs, query, where,
  Timestamp, runTransaction, getDoc, setDoc
} from 'firebase/firestore';
import { onAuthStateChanged } from 'firebase/auth';
import { auth, db } from '@/lib/firebase';
import { useBudgetManager } from './BudgetManagerContext';
import { useAccount } from './AccountContext';
import { useTransaction } from './TransactionContext';

// Define types
export type BillItem = {
  name: string;
  quantity: number;
  price: number;
  accountCode?: string;
  costCenter?: string;
  totalPrice?: number;
};

export type Bill = {
  id?: string;
  billNumber: number;
  vendorName: string;
  vendorEmail: string;
  vendorTaxId?: string;
  dueDate: Date;
  items: BillItem[];
  taxRate: number;
  notes: string;
  status: 'pending' | 'paid' | 'overdue';
  createdAt: Date;
  updatedAt: Date;
  createdBy: string;
  amount: number;
  budgetId: string;
  accountId: string;
  category: string;
  department?: string;
  parentCategory: string;
  purchaseOrderNumber?: string;
  attachmentUrl?: string;
  attachmentName?: string;
  reminderScheduled?: boolean;
  enableReminders?: boolean;
  reminderSettings?: {
    firstReminder: number;
    subsequentReminders: number;
    maxReminders: number;
  };
};

type BillState = {
  bills: Bill[];
  loading: boolean;
  error: string | null;
};

type BillAction =
  | { type: 'LOAD_BILLS'; payload: Bill[] }
  | { type: 'ADD_BILL'; payload: Bill }
  | { type: 'UPDATE_BILL'; payload: Bill }
  | { type: 'DELETE_BILL'; payload: string }
  | { type: 'SET_LOADING'; payload: boolean }
  | { type: 'SET_ERROR'; payload: string | null };

type BillContextType = {
  state: BillState;
  loadBills: () => Promise<void>;
  addBill: (bill: Omit<Bill, 'id' | 'createdAt' | 'updatedAt' | 'billNumber'>) => Promise<void>;
  updateBill: (bill: Bill) => Promise<void>;
  deleteBill: (billId: string) => Promise<void>;
  clearError: () => void;
};

const initialState: BillState = {
  bills: [],
  loading: false,
  error: null,
};

const BillContext = createContext<BillContextType | undefined>(undefined);

function billReducer(state: BillState, action: BillAction): BillState {
  switch (action.type) {
    case 'LOAD_BILLS':
      return { ...state, bills: action.payload, loading: false };
    case 'ADD_BILL':
      return { ...state, bills: [...state.bills, action.payload], loading: false };
    case 'UPDATE_BILL':
      return {
        ...state,
        bills: state.bills.map((bill) =>
          bill.id === action.payload.id ? action.payload : bill
        ),
        loading: false,
      };
    case 'DELETE_BILL':
      return {
        ...state,
        bills: state.bills.filter((bill) => bill.id !== action.payload),
        loading: false,
      };
    case 'SET_LOADING':
      return { ...state, loading: action.payload };
    case 'SET_ERROR':
      return { ...state, error: action.payload, loading: false };
    default:
      return state;
  }
}

export function BillProvider({ children }: { children: ReactNode }) {
  const [state, dispatch] = useReducer(billReducer, initialState);
  const { state: budgetState } = useBudgetManager();
  const { state: accountState } = useAccount();
  const { addTransaction } = useTransaction();

  const loadBills = useCallback(async () => {
    dispatch({ type: 'SET_LOADING', payload: true });
    try {
      const user = auth.currentUser;
      if (!user) throw new Error('User not authenticated');

      const currentBudget = budgetState.currentBudget;
      if (!currentBudget || !currentBudget.id) {
        console.warn('No budget selected or budget has no ID');
        dispatch({ type: 'LOAD_BILLS', payload: [] });
        return;
      }

      const billsQuery = query(
        collection(db, 'bills'),
        where('createdBy', '==', user.uid),
        where('budgetId', '==', currentBudget.id)
      );

      const querySnapshot = await getDocs(billsQuery);
      const bills: Bill[] = querySnapshot.docs.map((doc) => {
        const data = doc.data();
        return {
          ...data,
          id: doc.id,
          dueDate: data.dueDate.toDate(),
          createdAt: data.createdAt.toDate(),
          updatedAt: data.updatedAt.toDate(),
        } as Bill;
      });

      dispatch({ type: 'LOAD_BILLS', payload: bills });
    } catch (error) {
      console.error('Error loading bills:', error);
      dispatch({ type: 'SET_ERROR', payload: 'Failed to load bills' });
    }
  }, [budgetState.currentBudget]);

  const getNextBillNumber = async (budgetId: string): Promise<number> => {
    const counterRef = doc(db, 'bill_counters', budgetId);

    try {
      const newNumber = await runTransaction(db, async (transaction) => {
        const counterDoc = await transaction.get(counterRef);

        if (!counterDoc.exists()) {
          transaction.set(counterRef, {
            currentNumber: 1,
            budgetId: budgetId,
            createdBy: auth.currentUser?.uid,
            updatedAt: Timestamp.now()
          });
          return 1;
        }

        const currentNumber = counterDoc.data().currentNumber || 0;
        const nextNumber = currentNumber + 1;

        transaction.update(counterRef, {
          currentNumber: nextNumber,
          updatedAt: Timestamp.now()
        });

        return nextNumber;
      });

      return newNumber;
    } catch (error) {
      console.error("Error getting next bill number:", error);
      await setDoc(counterRef, {
        currentNumber: 1,
        budgetId: budgetId,
        createdBy: auth.currentUser?.uid,
        updatedAt: Timestamp.now()
      });
      return 1;
    }
  };

  const addBill = useCallback(async (bill: Omit<Bill, 'id' | 'createdAt' | 'updatedAt' | 'billNumber'>) => {
    dispatch({ type: 'SET_LOADING', payload: true });
    try {
      const user = auth.currentUser;
      if (!user) throw new Error('User not authenticated');
      const currentBudget = budgetState.currentBudget;
      if (!currentBudget) throw new Error('No budget selected');

      const nextBillNumber = await getNextBillNumber(currentBudget.id);

      // Calculate total amount
      const totalAmount = bill.items.reduce((sum, item) => 
        sum + (item.price * item.quantity), 0);

      const newBill = {
        ...bill,
        billNumber: nextBillNumber,
        createdBy: user.uid,
        budgetId: currentBudget.id,
        createdAt: Timestamp.now(),
        updatedAt: Timestamp.now(),
        amount: totalAmount
      };

      // Create the bill document
      const docRef = await addDoc(collection(db, 'bills'), newBill);
      const addedBill = {
        ...newBill,
        id: docRef.id,
        createdAt: new Date(),
        updatedAt: new Date(),
      };

      // If bill is paid, create expense transaction
      if (newBill.status === 'paid') {
        await addTransaction({
          description: `Bill #${nextBillNumber} - ${newBill.vendorName}`,
          amount: totalAmount,
          date: new Date(),
          category: newBill.category,
          parentCategory: newBill.parentCategory,
          type: 'expense',
          accountId: newBill.accountId
        });
      }

      dispatch({ type: 'ADD_BILL', payload: addedBill });
    } catch (error) {
      console.error('Error adding bill:', error);
      dispatch({ type: 'SET_ERROR', payload: 'Failed to add bill' });
      throw error;
    }
  }, [budgetState.currentBudget, addTransaction]);

  const updateBill = useCallback(async (bill: Bill) => {
    dispatch({ type: 'SET_LOADING', payload: true });
    try {
      if (!bill.id) throw new Error('Bill ID is required for update');

      const existingBill = state.bills.find(b => b.id === bill.id);
      if (!existingBill) throw new Error('Bill not found');

      // Calculate total amount
      const totalAmount = bill.items.reduce((sum, item) => 
        sum + (item.price * item.quantity), 0);

      // Handle transaction based on status change
      if (existingBill.status !== 'paid' && bill.status === 'paid') {
        await addTransaction({
          description: `Bill #${bill.billNumber} - ${bill.vendorName}`,
          amount: totalAmount,
          date: new Date(),
          category: bill.category,
          parentCategory: bill.parentCategory,
          type: 'expense',
          accountId: bill.accountId
        });
      }

      // Update the bill
      const { id, ...updateData } = bill;
      await updateDoc(doc(db, 'bills', id), {
        ...updateData,
        amount: totalAmount,
        updatedAt: Timestamp.now(),
      });

      dispatch({ type: 'UPDATE_BILL', payload: { ...bill, amount: totalAmount } });
    } catch (error) {
      console.error('Error updating bill:', error);
      dispatch({ type: 'SET_ERROR', payload: 'Failed to update bill' });
      throw error;
    }
  }, [state.bills, addTransaction]);

  const deleteBill = useCallback(async (billId: string) => {
    dispatch({ type: 'SET_LOADING', payload: true });
    try {
      await deleteDoc(doc(db, 'bills', billId));
      dispatch({ type: 'DELETE_BILL', payload: billId });
    } catch (error) {
      dispatch({ type: 'SET_ERROR', payload: 'Failed to delete bill' });
    }
  }, []);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      if (user) {
        loadBills();
      } else {
        dispatch({ type: 'LOAD_BILLS', payload: [] });
      }
    });

    return () => unsubscribe();
  }, [loadBills]);

  const clearError = useCallback(() => {
    dispatch({ type: 'SET_ERROR', payload: null });
  }, []);

  return (
    <BillContext.Provider
      value={{
        state,
        loadBills,
        addBill,
        updateBill,
        deleteBill,
        clearError,
      }}
    >
      {children}
    </BillContext.Provider>
  );
}

export function useBill(): BillContextType {
  const context = useContext(BillContext);
  if (!context) {
    throw new Error('useBill must be used within a BillProvider');
  }
  return context;
}